/* Markdown in a bonus title — currently one mark: whole-line strikethrough.
 *
 * A bonus sometimes has to be shown as gone: the seat cap on it was hit, or the
 * offer moved on, and the row has to read "this one is no longer included"
 * rather than quietly disappearing (a bonus that vanishes between two views of
 * the same page looks like a bug, or worse, like a bait-and-switch).
 *
 * The state has nowhere of its own to live. publishCheckoutConfig accepts a
 * fixed set of bonus fields and drops anything else, so a `struck: true`
 * alongside the name would be discarded on the way to Firestore and we can't
 * deploy functions right now. So the title carries it, in the notation people
 * already type for this: `~~Jason's daily market analysis~~`. The admin's
 * checkbox writes those tildes and the page reads them back — the stored string
 * is the whole state, which is also why pasting the markdown by hand works.
 *
 * Only the whole line counts. `~~a~~ and ~~b~~` is left alone rather than being
 * rendered as two struck fragments: this is a row-level flag wearing markdown's
 * clothes, not a markdown renderer, and half a struck title has no meaning on
 * the page. Anything that isn't the whole line stays verbatim text.
 *
 * CommonJS on purpose, same as bonus-value.js: scripts/bake-checkout-config.js
 * requires this from plain node at build time, and the admin editor imports it
 * through webpack.
 */

const STRIKE = "~~";

// Returns { text, struck }: the title as it should be displayed, and whether
// the row is struck out. `text` is always marker-free, so callers can render it
// without knowing the notation exists.
function parseBonusTitle(raw) {
  const text = String(raw == null ? "" : raw).trim();

  if (text.length <= 4 || !text.startsWith(STRIKE) || !text.endsWith(STRIKE)) {
    return { text, struck: false };
  }

  const inner = text.slice(STRIKE.length, -STRIKE.length);

  // A pair that closes and reopens ("~~a~~ and ~~b~~"), a run of three tildes
  // ("~~~x~~~"), or nothing but padding: none of those is a line struck out.
  // They stay verbatim text — wrong-looking on the page, which is the point:
  // a title that reads as markdown but isn't should say so rather than half
  // work.
  if (inner.includes(STRIKE) || inner.startsWith("~") || inner.endsWith("~")) {
    return { text, struck: false };
  }
  if (!inner.trim()) return { text, struck: false };

  // The markers can wrap padding — "~~ name ~~" is what you get from selecting
  // a title and typing tildes around it.
  return { text: inner.trim(), struck: true };
}

// The display text with any markers removed.
function bonusTitleText(raw) {
  return parseBonusTitle(raw).text;
}

// True when this title is marked struck.
function isBonusTitleStruck(raw) {
  return parseBonusTitle(raw).struck;
}

// Whether markers can be put around this title and read back off it. Only a
// title with tildes of its own fails — wrapping "~~a~~ and ~~b~~" would produce
// a string this file then refuses to read as struck, which is a checkbox that
// mangles the field and stays unticked. The admin disables the box instead.
function canStrikeBonusTitle(raw) {
  const { text } = parseBonusTitle(raw);
  if (!text) return true; // nothing to mark; marking it is a no-op, not a break
  return !text.includes(STRIKE) && !text.startsWith("~") && !text.endsWith("~");
}

// Adds or removes the markers, keeping the title itself untouched. Turning it
// on twice is the same as turning it on once. An empty title gets no markers —
// `~~~~` is not a struck anything, and it would publish as a name. A title that
// can't carry them comes back untouched rather than half-marked.
function setBonusTitleStruck(raw, struck) {
  const { text } = parseBonusTitle(raw);
  if (!text) return "";
  if (!struck) return text;
  return canStrikeBonusTitle(text) ? `${STRIKE}${text}${STRIKE}` : text;
}

// A bonus is struck if either language says so. In practice the admin's
// checkbox writes both at once, but a config edited by hand — or one where the
// 中文 title was filled in later — must not end up struck in one language and
// live in the other.
function isBonusStruck(bonus) {
  if (!bonus) return false;
  if (typeof bonus === "string") return isBonusTitleStruck(bonus);
  return isBonusTitleStruck(bonus.name) || isBonusTitleStruck(bonus.nameZh);
}

// Both titles have to be able to carry the mark, since the two are set
// together — a bonus that could be struck in English but not in 中文 would end
// up marked in one language and live in the other.
function canStrikeBonus(bonus) {
  if (!bonus) return false;
  if (typeof bonus === "string") return canStrikeBonusTitle(bonus);
  return canStrikeBonusTitle(bonus.name) && canStrikeBonusTitle(bonus.nameZh);
}

module.exports = {
  STRIKE,
  canStrikeBonus,
  parseBonusTitle,
  bonusTitleText,
  isBonusTitleStruck,
  canStrikeBonusTitle,
  setBonusTitleStruck,
  isBonusStruck,
};
